import React from 'react';
import { Link, Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, 
    faClock, 
    faExclamationCircle, 
    faInbox, 
    faLocationDot, 
    faShip, 
    faTag, 
    faTruck 
} from '@fortawesome/pro-light-svg-icons';
import { NavLink } from 'react-router-dom';
import styles from '../styles.module.scss';
import { IOperationItem } from '../types';
import Text from '../../../typography/text';
import { OrderBadge } from './orderBadge';

interface IWorkspaceTableViewProps {
    items: IOperationItem[];
    onQuickStatus(item: IOperationItem): void;
    onReschedule(item: IOperationItem): void;
}

const WorkspaceTableView: React.FC<IWorkspaceTableViewProps> = ({ items, onQuickStatus, onReschedule }) => {
    if (items.length === 0) {
        return (
            <div className={styles.tableWrapper}>
                <div className={styles.emptyState}>
                    <FontAwesomeIcon icon={faInbox} style={{ fontSize: 36 }} />
                    <Text size="medium" style={{ fontWeight: 600 }}>Tidak ada aktivitas operasional</Text>
                    <Text size="small">Semua jadwal pada filter ini telah terselesaikan atau tidak ada data jadwal.</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.tableWrapper}>
            <table className={styles.denseTable}>
                <thead>
                    <tr>
                        <th style={{ width: '160px' }}>Aktivitas & Status</th>
                        <th style={{ minWidth: '180px' }}>Armada / Kapal & Voy</th>
                        <th style={{ minWidth: '160px' }}>Order & Customer</th>
                        <th style={{ minWidth: '200px' }}>Rute & Kontainer</th>
                        <th style={{ minWidth: '170px' }}>Jadwal / Waktu</th>
                        <th style={{ width: '190px', textAlign: 'center' }}>Aksi Cepat</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => {
                        const isOverdue = item.isOverdue;

                        return (
                            <tr 
                                key={item.id} 
                                className={isOverdue ? styles.rowOverdue : ''}
                            >
                                {/* 1. Aktivitas & Status */}
                                <td>
                                    <Stack tokens={{ childrenGap: 6 }}>
                                        <span 
                                            className={styles.activityBadge}
                                            style={{
                                                background: item.activityColor + '15',
                                                color: item.activityColor,
                                                border: `1px solid ${item.activityColor}35`,
                                            }}
                                        >
                                            <FontAwesomeIcon icon={item.activityIcon} />
                                            {item.activityLabel}
                                        </span>

                                        <span 
                                            className={styles.statusBadge}
                                            style={{
                                                background: item.normalizedStatus === 'completed' ? '#dcfce7' : (item.normalizedStatus === 'in_progress' ? (item.status === 'sailing' ? '#ccfbf1' : '#e0f2fe') : '#f1f5f9'),
                                                color: item.normalizedStatus === 'completed' ? '#16a34a' : (item.normalizedStatus === 'in_progress' ? (item.status === 'sailing' ? '#0f766e' : '#0284c7') : '#64748b'),
                                                border: item.normalizedStatus === 'completed' ? '1px solid #bbf7d0' : (item.normalizedStatus === 'in_progress' ? (item.status === 'sailing' ? '1px solid #99f6e4' : '1px solid #bae6fd') : '1px solid #e2e8f0'),
                                            }}
                                        >
                                            <span 
                                                style={{ 
                                                    width: 6, 
                                                    height: 6, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: item.normalizedStatus === 'completed' ? '#22c55e' : (item.normalizedStatus === 'in_progress' ? (item.status === 'sailing' ? '#14b8a6' : '#0284c7') : '#94a3b8'),
                                                    display: 'inline-block' 
                                                }} 
                                            />
                                            {item.statusLabel}
                                        </span>
                                    </Stack>
                                </td>

                                {/* 2. Armada / Kapal & Voy */}
                                <td>
                                    <Stack tokens={{ childrenGap: 2 }}>
                                        {item.shipName ? (
                                            <>
                                                <NavLink to={item.detailsLink} style={{ textDecoration: 'none', color: '#0284c7', fontWeight: 600 }}>
                                                    <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                                        {item.shipName}
                                                    </Text>
                                                </NavLink>
                                                {item.voy && (
                                                    <Text size="xsmall" style={{ color: '#64748b' }}>
                                                        Voy: {item.voy} {item.vendorName ? `(${item.vendorName})` : ''}
                                                    </Text>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                                    {item.truckRegNumber || item.vendorName || '-'}
                                                </Text>
                                                {item.driverName && (
                                                    <Text size="xsmall" style={{ color: '#64748b' }}>
                                                        Driver: {item.driverName} {item.driverPhone ? `(${item.driverPhone})` : ''}
                                                    </Text>
                                                )}
                                            </>
                                        )}
                                    </Stack>
                                </td>

                                {/* 3. Order & Customer */}
                                <td>
                                    <OrderBadge item={item} />
                                </td>

                                {/* 4. Rute & Kontainer */}
                                <td>
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        {item.locationText && (
                                            <Text size="small" style={{ color: '#334155' }}>
                                                📍 {item.locationText}
                                            </Text>
                                        )}

                                        {item.containers && item.containers.length > 0 && (
                                            <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                                                {item.containers.slice(0, 3).map((c, idx) => (
                                                    <span 
                                                        key={idx}
                                                        style={{
                                                            fontSize: '11px',
                                                            background: '#f1f5f9',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '4px',
                                                            padding: '1px 6px',
                                                            color: '#334155',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {c.containerNumber}
                                                    </span>
                                                ))}
                                                {item.containers.length > 3 && (
                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                        +{item.containers.length - 3} lainnya
                                                    </span>
                                                )}
                                            </Stack>
                                        )}
                                    </Stack>
                                </td>

                                {/* 5. Jadwal / Waktu */}
                                <td>
                                    <Stack tokens={{ childrenGap: 3 }}>
                                        <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                            {item.scheduledDateFormatted}
                                        </Text>

                                        {item.estimatedArrival && (
                                            <Text size="xsmall" style={{ color: '#64748b' }}>
                                                ETA: {item.estimatedArrival}
                                            </Text>
                                        )}

                                        {isOverdue && (
                                            <span className={styles.overdueTag}>
                                                <FontAwesomeIcon icon={faExclamationCircle} />
                                                Overdue {item.overdueDays ? `${item.overdueDays} hari` : ''}
                                            </span>
                                        )}
                                    </Stack>
                                </td>

                                {/* 6. Aksi Cepat (Quick Actions) */}
                                <td>
                                    <div className={styles.actionButtonGroup} style={{ justifyContent: 'center' }}>
                                        {/* Tombol Centang: Quick Status Update */}
                                        <button
                                            className={styles.btnActionCheck}
                                            onClick={() => onQuickStatus(item)}
                                            title="Mark Active / Update Status"
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                            <span>Update</span>
                                        </button>

                                        {/* Tombol Jam: Quick Reschedule */}
                                        <button
                                            className={styles.btnActionReschedule}
                                            onClick={() => onReschedule(item)}
                                            title="Reschedule / Jadwal Ulang"
                                        >
                                            <FontAwesomeIcon icon={faClock} />
                                            <span>Reschedule</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default WorkspaceTableView;
